/**
 * AutosaveIndicator.jsx
 *
 * Small, non-intrusive status indicator for autosave state.
 * Renders a badge in the editor toolbar showing whether the
 * document is saved, saving, offline, or errored.
 *
 * Props:
 *   status — 'idle' | 'pending' | 'saving' | 'offline' | 'retry' | 'error'
 *   theme  — 'dark' | 'light'
 *
 * Usage:
 *   <AutosaveIndicator status={autosaveStatus} theme={theme} />
 */
import React, { useEffect, useState } from 'react';

const STATUS_CONFIG = {
  idle: {
    icon: '✓',
    label: 'Saved',
    color: '#22c55e',
    bg: 'rgba(34,197,94,0.08)',
    border: 'rgba(34,197,94,0.2)',
    pulse: false,
  },
  pending: {
    icon: '●',
    label: 'Unsaved',
    color: '#f59e0b',
    bg: 'rgba(245,158,11,0.08)',
    border: 'rgba(245,158,11,0.2)',
    pulse: true,
  },
  saving: {
    icon: '⟳',
    label: 'Saving...',
    color: '#60a5fa',
    bg: 'rgba(59,130,246,0.08)',
    border: 'rgba(59,130,246,0.2)',
    pulse: true,
  },
  offline: {
    icon: '⚠',
    label: 'Offline',
    color: '#f97316',
    bg: 'rgba(249,115,22,0.08)',
    border: 'rgba(249,115,22,0.2)',
    pulse: false,
  },
  retry: {
    icon: '↺',
    label: 'Retrying...',
    color: '#a78bfa',
    bg: 'rgba(167,139,250,0.08)',
    border: 'rgba(167,139,250,0.2)',
    pulse: true,
  },
  error: {
    icon: '✕',
    label: 'Failed to save',
    color: '#f87171',
    bg: 'rgba(248,113,113,0.08)',
    border: 'rgba(248,113,113,0.2)',
    pulse: false,
  },
};

export default function AutosaveIndicator({ status = 'idle', theme = 'dark' }) {
  const [visible, setVisible] = useState(true);
  const [prevStatus, setPrevStatus] = useState(status);

  // Auto-hide 'idle' (saved) state after 4 seconds
  useEffect(() => {
    if (status === 'idle') {
      const t = setTimeout(() => setVisible(false), 4000);
      return () => clearTimeout(t);
    } else {
      setVisible(true);
    }
  }, [status]);

  // Show again whenever status changes
  useEffect(() => {
    if (status !== prevStatus) {
      setVisible(true);
      setPrevStatus(status);
    }
  }, [status, prevStatus]);

  if (!visible) return null;

  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.idle;

  return (
    <div
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 5,
        padding: '4px 10px',
        borderRadius: 8,
        background: cfg.bg,
        border: `1px solid ${cfg.border}`,
        fontSize: 11,
        fontWeight: 600,
        color: cfg.color,
        fontFamily: "'Inter', sans-serif",
        transition: 'all 0.3s ease',
        userSelect: 'none',
        whiteSpace: 'nowrap',
      }}
      title={`Autosave: ${cfg.label}`}
    >
      <span style={{
        fontSize: 12,
        animation: cfg.pulse ? 'asi-pulse 1.4s ease-in-out infinite' : 'none',
        display: 'inline-block',
      }}>
        {cfg.icon}
      </span>
      <span>{cfg.label}</span>

      <style>{`
        @keyframes asi-pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.4; }
        }
      `}</style>
    </div>
  );
}
